import { createRouter, createWebHistory } from 'vue-router'
import { resetScroll } from '../utils/scrollUtils.js'
import SplashScreen from '../views/SplashScreen.vue'
import OnboardingFlow from '../views/OnboardingFlow.vue'
import WelcomeScreen from '../views/WelcomeScreen.vue'
import ProfileScreen from '../views/ProfileScreen.vue'
import DashboardScreen from '../views/DashboardScreen.vue'
import MarketsScreen from '../views/MarketsScreen.vue'

const routes = [
  {
    path: '/',
    name: 'Splash',
    component: SplashScreen
  },
  {
    path: '/onboarding',
    name: 'Onboarding',
    component: OnboardingFlow
  },
  {
    path: '/welcome',
    name: 'Welcome',
    component: WelcomeScreen
  },
  {
    path: '/profile',
    name: 'Profile',
    component: ProfileScreen
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: DashboardScreen
  },
  {
    path: '/markets',
    name: 'Markets',
    component: MarketsScreen
  },
  {
    path: '/positions-history',
    name: 'PositionsHistory',
    component: () => import('../views/PositionsHistoryScreen.vue')
  },
  {
    path: '/markets/:id',
    name: 'MarketDetails',
    component: () => import('../views/MarketDetailsScreen.vue')
  },
  {
    path: '/airdrop',
    name: 'Airdrop',
    component: () => import('../views/AirdropScreen.vue')
  },
  {
    path: '/trade',
    name: 'Trade',
    component: () => import('../views/TradeScreen.vue')
  },
  {
    path: '/chart',
    name: 'Chart',
    component: () => import('../views/ChartScreen.vue')
  },
  {
    path: '/trader/:id',
    name: 'TraderDetails',
    component: () => import('../views/TraderDetailsScreen.vue')
  },
  {
    path: '/faq',
    name: 'FAQ',
    component: () => import('../views/FAQScreen.vue')
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('../views/SettingsScreen.vue')
  },
  {
    path: '/withdraw',
    name: 'Withdraw',
    component: () => import('../views/WithdrawScreen.vue')
  },
  {
    path: '/deposit',
    name: 'Deposit',
    component: () => import('../views/DepositScreen.vue')
  },
  {
    path: '/transaction-history',
    name: 'TransactionHistory',
    component: () => import('../views/TransactionHistoryScreen.vue')
  },
  {
    path: '/levels',
    name: 'Levels',
    component: () => import('../views/LevelsScreen.vue')
  },
  {
    path: '/balance-details',
    name: 'BalanceDetails',
    component: () => import('../views/BalanceDetailsScreen.vue')
  },
  {
    path: '/admin',
    name: 'Admin',
    component: () => import('../views/AdminPanel.vue'),
    meta: { requiresOnboarding: true },
    redirect: '/admin/leads'
  },
  // Dashboard вкладка закомментирована
  // {
  //   path: '/admin/dashboard',
  //   name: 'AdminDashboard',
  //   component: () => import('../views/admin/AdminDashboardScreen.vue'),
  //   meta: { requiresOnboarding: true }
  // },
  {
    path: '/admin/leads',
    name: 'AdminLeads',
    component: () => import('../views/admin/LeadsScreen.vue'),
    meta: { requiresOnboarding: true }
  },
  {
    path: '/admin/leads/:userId',
    name: 'AdminLeadDetails',
    component: () => import('../views/admin/LeadDetailsScreen.vue'),
    meta: { requiresOnboarding: true }
  },
  {
    path: '/admin/lead/:userId/positions-history',
    name: 'AdminLeadPositionsHistory',
    component: () => import('../views/admin/LeadPositionsHistoryScreen.vue'),
    meta: { requiresOnboarding: true }
  },
  {
    path: '/admin/lead/:userId/transactions',
    name: 'AdminLeadTransactions',
    component: () => import('../views/admin/LeadTransactionHistoryScreen.vue'),
    meta: { requiresOnboarding: true }
  },
  {
    path: '/admin/landings',
    name: 'AdminLandings',
    component: () => import('../views/admin/LandingsScreen.vue'),
    meta: { requiresOnboarding: true }
  },
  {
    path: '/admin/workers',
    name: 'AdminWorkers',
    component: () => import('../views/admin/WorkersScreen.vue'),
    meta: { requiresOnboarding: true }
  }
]

// Предзагрузка компонентов для кеширования
const preloadComponents = async () => {
  const components = [
    () => import('../views/FAQScreen.vue'),
    () => import('../views/TradeScreen.vue'),
    () => import('../views/ChartScreen.vue'),
    () => import('../views/TraderDetailsScreen.vue'),
    () => import('../views/ProfileScreen.vue'),
    () => import('../views/DashboardScreen.vue'),
    () => import('../views/MarketsScreen.vue'),
    () => import('../views/MarketDetailsScreen.vue'),
    () => import('../views/PositionsHistoryScreen.vue'),
    () => import('../views/BalanceDetailsScreen.vue'),
    () => import('../views/OnboardingFlow.vue'),
    () => import('../views/WelcomeScreen.vue'),
    () => import('../views/AdminPanel.vue'),
    // () => import('../views/admin/AdminDashboardScreen.vue'), // Dashboard закомментирован
    () => import('../views/admin/LeadsScreen.vue'),
    () => import('../views/admin/LeadDetailsScreen.vue'),
    () => import('../views/admin/LeadPositionsHistoryScreen.vue'),
    () => import('../views/admin/LeadTransactionHistoryScreen.vue'),
    () => import('../views/admin/LandingsScreen.vue'),
    () => import('../views/admin/WorkersScreen.vue'),
    () => import('../components/ui/BottomMenu.vue'),
    () => import('../components/ui/BottomMenuAdmin.vue'),
    () => import('../components/ui/BaseCard.vue'),
    () => import('../components/ui/BaseButton.vue'),
    () => import('../components/ui/PinInput.vue'),
    () => import('../components/ui/MarketSortBar.vue'),
    () => import('../components/ui/MarketListItem.vue')
  ]
  
  // Предзагружаем все компоненты параллельно
  await Promise.all(components.map(component => component().catch(() => null)))
}

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    // Принудительно сбрасываем скролл при переходе на новый маршрут
    return new Promise((resolve) => {
      setTimeout(() => {
        resetScroll()
        resolve({ top: 0, left: 0 })
      }, 0)
    })
  }
})

const TEST_MODE = process.env.VUE_APP_TEST_MODE === 'true'
const TEST_USER_ID = process.env.VUE_APP_TEST_USER_ID || null

// Навигационный guard для проверки статуса воркера на роутах /admin и дочерних
router.beforeEach(async (to, from, next) => {
  // Проверяем только роуты админ панели
  if (to.path.startsWith('/admin')) {
    try {
      // Получаем user_id из Telegram WebApp
      const { apiService } = await import('../services/api.js')
      
      // Динамически получаем user_id
      let userId = null
      if (TEST_MODE && TEST_USER_ID) {
        userId = TEST_USER_ID
      } else if (typeof window !== 'undefined') {
        // Пробуем получить из Telegram WebApp
        const tg = window.Telegram?.WebApp
        if (tg && tg.initDataUnsafe && tg.initDataUnsafe.user) {
          userId = tg.initDataUnsafe.user.id
        }
      }

      if (userId) {
        const response = await apiService.checkWorker(userId)
        
        // Если пользователь больше не воркер, перенаправляем на профиль
        if (!response.data || response.data.worker !== true) {
          next({ name: 'Profile' })
          return
        }
      }
    } catch (error) {
      console.error('Error checking worker status in router guard:', error)
      // В случае ошибки разрешаем переход, чтобы не блокировать пользователя
    }
  }
  
  next()
})

// Предзагружаем компоненты при инициализации роутера
preloadComponents()

export default router
export { preloadComponents }
